import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, Fade } from '@mui/material';
import logging from 'utils/logging';

const log = logging.getLogger('ClosedCaptioning');

const CaptionContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: '80%',
  minWidth: '40%',
  padding: theme.spacing(2),
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#ffffff',
  borderRadius: theme.spacing(1),
  zIndex: 1400,
  textAlign: 'center',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: '1.25rem',
  maxHeight: '30vh',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255, 255, 255, 0.5)',
  }
}));

/**
 * ClosedCaptioning component for displaying narration text
 * 
 * @param {Object} props
 * @param {string} props.text - The narration/caption text to display
 * @param {boolean} props.visible - Whether the caption is currently visible
 * @param {number} [props.duration] - Optional auto-hide duration in ms
 * @param {Function} [props.onHide] - Callback when the caption is dismissed/auto-hides
 * @param {Object} [props.style] - Optional style overrides
 * @param {string} [props.className] - Optional className for custom styling
 */
const stripMarkdown = (str) => {
  if (!str) return str;
  return str
    .replace(/\*\*([^*]+)\*\*/g, '$1')  // **bold**
    .replace(/\*([^*]+)\*/g, '$1')       // *italic*
    .replace(/__([^_]+)__/g, '$1')       // __bold__
    .replace(/_([^_]+)_/g, '$1')         // _italic_
    .replace(/[""]/g, '"');              // smart quotes
};

const ClosedCaptioning = ({
  text,
  visible,
  duration,
  onHide,
  style,
  className,
}) => {
  const timerRef = useRef(null);

  // Set up auto-hide timer if duration is provided
  useEffect(() => {
    log.debug(`Captioning visible: ${visible}, duration: ${duration}, onHide: ${onHide}`);
    if (visible && duration && onHide) {
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      // Set new timer
      timerRef.current = setTimeout(() => {
        log.debug(`Auto-hiding caption after ${duration}ms`);
        onHide();
      }, duration);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [visible, duration, onHide]);

  return (
    <Fade in={visible} timeout={500}>
      <Box display={visible ? 'block' : 'none'}>
        <CaptionContainer
          elevation={3}
          style={style}
          className={className}
          role="region"
          aria-live="assertive"
        >
          {stripMarkdown(text)}
        </CaptionContainer>
      </Box>
    </Fade>
  );
};

export default ClosedCaptioning; 